import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronDown, Check, X, Search } from 'lucide-react';

/**
 * Dropdown - Premium animated dropdown select
 *
 * Props:
 *  - options: Array<{ label, value, icon? }>
 *  - value: string | string[] (multi-select when multiple=true)
 *  - onChange: (value) => void
 *  - placeholder: string
 *  - multiple: boolean (enables multi-select with checkboxes)
 *  - searchable: boolean (adds search filter inside dropdown)
 *  - clearable: boolean (show clear button)
 *  - disabled: boolean
 *  - label: string (floating label)
 *  - size: 'sm' | 'md' | 'lg'
 *  - variant: 'default' | 'filled' | 'outlined'
 *  - id: string
 *  - className: string
 */
export default function Dropdown({
  options = [],
  value,
  onChange,
  placeholder = 'Select option',
  multiple = false,
  searchable = false,
  clearable = false,
  disabled = false,
  label,
  size = 'md',
  variant = 'default',
  id = 'dropdown',
  className = '',
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef(null);
  const searchRef = useRef(null);

  // Normalise value
  const selected = multiple
    ? Array.isArray(value) ? value : []
    : value ?? '';

  const displayLabel = multiple
    ? selected.length === 0
      ? placeholder
      : selected.length === 1
        ? options.find(o => o.value === selected[0])?.label ?? selected[0]
        : `${selected.length} selected`
    : options.find(o => o.value === selected)?.label ?? placeholder;

  const hasValue = multiple ? selected.length > 0 : !!selected;

  const filteredOptions = search
    ? options.filter(o => o.label.toLowerCase().includes(search.toLowerCase()))
    : options;

  const open = () => {
    if (!disabled) {
      setIsOpen(true);
      setTimeout(() => searchRef.current?.focus(), 50);
    }
  };

  const close = useCallback(() => {
    setIsOpen(false);
    setSearch('');
  }, []);

  const toggle = () => (isOpen ? close() : open());

  const select = (option) => {
    if (multiple) {
      const next = selected.includes(option.value)
        ? selected.filter(v => v !== option.value)
        : [...selected, option.value];
      onChange?.(next);
    } else {
      onChange?.(option.value);
      close();
    }
  };

  const clear = (e) => {
    e.stopPropagation();
    onChange?.(multiple ? [] : '');
  };

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (!containerRef.current?.contains(e.target)) close();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [close]);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [close]);

  return (
    <div
      ref={containerRef}
      className={`ui-dropdown ${size} ${variant} ${isOpen ? 'open' : ''} ${disabled ? 'disabled' : ''} ${hasValue ? 'has-value' : ''} ${className}`}
      id={id}
    >
      {label && (
        <label className={`ui-dropdown-label ${hasValue || isOpen ? 'active' : ''}`}>
          {label}
        </label>
      )}

      <button
        type="button"
        className="ui-dropdown-trigger"
        onClick={toggle}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={label || placeholder}
      >
        <span className={`ui-dropdown-value ${!hasValue ? 'placeholder' : ''}`}>
          {displayLabel}
        </span>
        <div className="ui-dropdown-adornments">
          {clearable && hasValue && (
            <span className="ui-dropdown-clear" onClick={clear} role="button" aria-label="Clear">
              <X size={14} />
            </span>
          )}
          <ChevronDown size={16} className="ui-dropdown-chevron" />
        </div>
      </button>

      {isOpen && (
        <div className="ui-dropdown-menu" role="listbox" aria-multiselectable={multiple}>
          {searchable && (
            <div className="ui-dropdown-search-wrap">
              <Search size={14} className="ui-dropdown-search-icon" />
              <input
                ref={searchRef}
                type="text"
                className="ui-dropdown-search"
                placeholder="Search options..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                onClick={e => e.stopPropagation()}
              />
            </div>
          )}

          <div className="ui-dropdown-list">
            {filteredOptions.length === 0 ? (
              <div className="ui-dropdown-empty">No options found</div>
            ) : (
              filteredOptions.map((option) => {
                const isSelected = multiple
                  ? selected.includes(option.value)
                  : selected === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    className={`ui-dropdown-option ${isSelected ? 'selected' : ''}`}
                    onClick={() => select(option)}
                    role="option"
                    aria-selected={isSelected}
                  >
                    {multiple && (
                      <span className={`ui-dropdown-checkbox ${isSelected ? 'checked' : ''}`}>
                        {isSelected && <Check size={11} />}
                      </span>
                    )}
                    {option.icon && <span className="ui-dropdown-opt-icon">{option.icon}</span>}
                    <span className="ui-dropdown-opt-label">{option.label}</span>
                    {!multiple && isSelected && (
                      <Check size={14} className="ui-dropdown-check-mark" />
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
